/**
 * Splister API Client
 * Configured fetch wrapper with auth, error handling, and rate limiting
 */

import type { ApiError, RateLimitInfo } from './types';

export interface ClientConfig {
  baseUrl: string;
  getAccessToken: () => string | null;
  onUnauthorized?: (code?: string) => void;
  onForbidden?: (code: string) => void;
  onRateLimited?: (info: RateLimitInfo) => void;
}

// Returned by the backend when the JWT is valid but the Spotify access token
// hasn't landed in Redis yet (e.g. request racing the OAuth callback write).
// This is transient and worth a short retry instead of nuking the session.
const TRANSIENT_AUTH_ERROR_CODE = 'AUTHENTICATION_ERROR';
const AUTH_RETRY_ATTEMPTS = 3;
const AUTH_RETRY_DELAY_MS = 400;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class ApiClient {
  private baseUrl: string;
  private getAccessToken: () => string | null;
  private onUnauthorized?: (code?: string) => void;
  private onForbidden?: (code: string) => void;
  private onRateLimited?: (info: RateLimitInfo) => void;

  constructor(config: ClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.getAccessToken = config.getAccessToken;
    this.onUnauthorized = config.onUnauthorized;
    this.onForbidden = config.onForbidden;
    this.onRateLimited = config.onRateLimited;
  }

  private getHeaders(): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
    });

    const token = this.getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  private extractRateLimitInfo(response: Response): RateLimitInfo | null {
    const limit = response.headers.get('X-RateLimit-Limit');
    const remaining = response.headers.get('X-RateLimit-Remaining');
    const reset = response.headers.get('X-RateLimit-Reset');

    if (limit && remaining && reset) {
      return {
        limit: parseInt(limit, 10),
        remaining: parseInt(remaining, 10),
        reset: parseInt(reset, 10),
      };
    }
    return null;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    // Handle rate limiting
    if (response.status === 429) {
      const info = this.extractRateLimitInfo(response);
      if (info && this.onRateLimited) {
        this.onRateLimited(info);
      }
      const error = await response.json() as ApiError;
      throw new ApiClientError(error.error.code, error.error.message, 429);
    }

    // Handle forbidden (whitelist check)
    if (response.status === 403) {
      const error = await response.json() as ApiError;
      if (this.onForbidden && error.error.code) {
        this.onForbidden(error.error.code);
      }
      throw new ApiClientError(
        error.error.code,
        error.error.message,
        403,
        error.error.details
      );
    }

    // Handle no content
    if (response.status === 204) {
      return undefined as T;
    }

    // Handle error responses
    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
        const error = JSON.parse(errorText) as ApiError;
        throw new ApiClientError(
          error.error?.code || 'UNKNOWN_ERROR',
          error.error?.message || 'An error occurred',
          response.status,
          error.error?.details
        );
      } catch (parseError) {
        // If parsing fails, use the raw text
        console.error('Failed to parse error response:', errorText);
        throw new ApiClientError(
          'UNKNOWN_ERROR',
          errorText || `HTTP ${response.status} error`,
          response.status
        );
      }
    }

    return response.json() as Promise<T>;
  }

  /**
   * Execute a request, transparently retrying a few times on a transient
   * "Spotify token not found yet" 401 (AUTHENTICATION_ERROR) instead of
   * immediately treating it as a dead session. This happens right after
   * OAuth login when a data request races the backend's Redis token write.
   *
   * Any other 401 (invalid/expired/revoked JWT) still fails immediately
   * and calls onUnauthorized.
   */
  private async requestWithAuthRetry<T>(execute: () => Promise<Response>): Promise<T> {
    for (let attempt = 1; attempt <= AUTH_RETRY_ATTEMPTS; attempt++) {
      const response = await execute();

      if (response.status === 401) {
        const error = await response.json() as ApiError;
        const isTransient = error.error?.code === TRANSIENT_AUTH_ERROR_CODE;

        if (isTransient && attempt < AUTH_RETRY_ATTEMPTS) {
          await delay(AUTH_RETRY_DELAY_MS);
          continue;
        }

        if (this.onUnauthorized) {
          this.onUnauthorized(error.error?.code);
        }
        throw new ApiClientError(
          error.error?.code || 'UNAUTHORIZED',
          error.error?.message || 'Unauthorized',
          401
        );
      }

      return this.handleResponse<T>(response);
    }

    // Unreachable: loop always returns or throws above.
    throw new ApiClientError('UNAUTHORIZED', 'Unauthorized', 401);
  }

  async get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    return this.requestWithAuthRetry<T>(() =>
      fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
      })
    );
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.requestWithAuthRetry<T>(() =>
      fetch(`${this.baseUrl}${path}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
      })
    );
  }

  async postFormData<T>(path: string, formData: FormData): Promise<T> {
    return this.requestWithAuthRetry<T>(() => {
      const headers = new Headers();
      const token = this.getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      // Don't set Content-Type - browser will set it with boundary
      return fetch(`${this.baseUrl}${path}`, {
        method: 'POST',
        headers,
        body: formData,
      });
    });
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.requestWithAuthRetry<T>(() =>
      fetch(`${this.baseUrl}${path}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
      })
    );
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.requestWithAuthRetry<T>(() =>
      fetch(`${this.baseUrl}${path}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify(body),
      })
    );
  }

  async delete<T>(path: string, body?: unknown): Promise<T> {
    return this.requestWithAuthRetry<T>(() => {
      const options: RequestInit = {
        method: 'DELETE',
        headers: this.getHeaders(),
      };
      if (body) {
        options.body = JSON.stringify(body);
      }
      return fetch(`${this.baseUrl}${path}`, options);
    });
  }
}

export class ApiClientError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// Singleton instance
let clientInstance: ApiClient | null = null;

export function createApiClient(config: ClientConfig): ApiClient {
  clientInstance = new ApiClient(config);
  return clientInstance;
}

export function getApiClient(): ApiClient {
  if (!clientInstance) {
    throw new Error('API client not initialized. Call createApiClient first.');
  }
  return clientInstance;
}

/**
 * Initialize API client with configuration
 * Called once at app startup
 */
export interface InitializeConfig {
  baseUrl: string;
  getAccessToken: () => string | null;
  onUnauthorized?: (code?: string) => void;
  onForbidden?: (code: string) => void;
  onRateLimited?: (info: RateLimitInfo) => void;
}

export function initializeApiClient(config: InitializeConfig): ApiClient {
  return createApiClient(config);
}

/**
 * Get dynamic API base URL using page protocol/host
 * Fixes mixed content issues in production without env vars
 */
export function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (typeof window === 'undefined') return 'http://localhost:8000/v1';
  const { protocol, host } = window.location;
  // Use HTTPS for non-localhost hosts to prevent mixed content on HTTP→HTTPS redirect
  const isLocalhost = host.includes('localhost') || host.includes('127.0.0.1');
  const safeProtocol = isLocalhost ? protocol : 'https:';
  return `${safeProtocol}//${host}/v1`;
}

/**
 * Get dynamic API URL (without /v1 suffix) using page protocol/host
 */
export function getApiUrl(): string {
  if (import.meta.env['VITE_API_URL']) {
    return import.meta.env['VITE_API_URL'];
  }
  if (typeof window === 'undefined') return 'http://localhost:8000';
  const { protocol, host } = window.location;
  // Use HTTPS for non-localhost hosts to prevent mixed content on HTTP→HTTPS redirect
  const isLocalhost = host.includes('localhost') || host.includes('127.0.0.1');
  const safeProtocol = isLocalhost ? protocol : 'https:';
  return `${safeProtocol}//${host}`;
}

/**
 * Get WebSocket URL using page protocol/host
 * Uses VITE_API_URL env var if set (for dev with separate frontend/backend servers)
 */
export function getWsUrl(): string {
  // Use env var if set (for dev with separate frontend/backend servers)
  if (import.meta.env['VITE_API_URL']) {
    // Convert http:// or https:// to ws:// or wss://
    // Preserve secure protocol (https -> wss) for security
    const url = import.meta.env['VITE_API_URL'];
    const isSecure = url.startsWith('https://');
    return url.replace(/^https?:/, isSecure ? 'wss:' : 'ws:');
  }

  if (typeof window === 'undefined') return 'ws://localhost:8000';
  const { protocol, host } = window.location;
  // Use WSS for non-localhost hosts to prevent mixed content on HTTP→HTTPS redirect
  const isLocalhost = host.includes('localhost') || host.includes('127.0.0.1');
  const wsProtocol = (protocol === 'https:' || !isLocalhost) ? 'wss:' : 'ws:';
  return `${wsProtocol}//${host}`;
}
